import { failedInvoices } from '@/app/dashboard/invoicing/failed-deliveries/utils';
import { getPerms } from '@/lib/perms';
import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import { get } from 'lodash';
import { useMemo } from 'react';
import {
  getIdentifierMapping,
  type Identifier,
  type IdentifierInfo,
} from './data';
import type { StatisticsToHandle } from './types';
import { buildQueryForStatistics } from './utils';
import { useSession } from '@/providers/session-provider';

// Helper function to create a StatisticsToHandle-compatible query for failed invoices
function buildFailedInvoicesQuery(
  identifier: Identifier,
  queryOptions: Omit<
    UseQueryOptions<StatisticsToHandle>,
    'queryKey' | 'queryFn'
  >
): UseQueryOptions<StatisticsToHandle> {
  return {
    queryKey: ['failedInvoices', identifier, 'statistics'],
    queryFn: async () => {
      const baseQuery = failedInvoices({
        countOnly: true,
        identifier,
      });
      const result = await baseQuery.queryFn();

      // Transform CountResponse to StatisticsToHandle
      return {
        identifier,
        filters: {},
        modelName: 'invoice' as const,
        count:
          typeof result === 'object' && 'count' in result ? result.count : 0,
        plainUrlReturn: false,
      };
    },
    ...queryOptions,
  };
}

export const useGetStatisticsToHandle = (
  options: Omit<UseQueryOptions<StatisticsToHandle>, 'queryKey' | 'queryFn'> & {
    identifierString: string;
  }
) => {
  const {
    data: {
      session: { userId },
    },
  } = useSession();

  const { identifierString, ...queryOptions } = options || {};
  const identifiers = identifierString?.split(',') as Identifier[];

  const batchedQueries = useMemo(() => {
    const requests = [];
    const identifierMapping = getIdentifierMapping(userId);
    for (const identifier of identifiers) {
      const {
        modelName,
        filters,
        content_type: contentType,
        noFilterReturn,
        plainUrlReturn,
        group_modelNames: groupModelNames,
        group_content_types: groupContentType,
        overrideUrl,
      } = get(identifierMapping, identifier, {}) as IdentifierInfo;
      if (groupModelNames) {
        for (const groupModelName of groupModelNames) {
          const { canView } = getPerms(groupModelName);
          if (canView) {
            requests.push(
              buildQueryForStatistics({
                modelName: groupModelName,
                identifier,
                contentType: groupContentType,
                filters,
                noFilterReturn,
                plainUrlReturn,
                enableGlobalFilters: true,
                ...queryOptions,
              })
            );
          }
        }
        continue;
      }
      if (modelName) {
        const { canView } = getPerms(modelName);
        if (canView) {
          requests.push(
            buildQueryForStatistics({
              modelName,
              identifier,
              contentType: groupContentType || contentType,
              filters,
              noFilterReturn,
              plainUrlReturn,
              enableGlobalFilters: true,
              ...queryOptions,
            })
          );
        }
      }
      if (overrideUrl && identifier.includes('invoice_sendout_failed')) {
        const { canView: canViewInvoice } = getPerms('invoice');
        if (canViewInvoice) {
          requests.push(buildFailedInvoicesQuery(identifier, queryOptions));
        }
      }
    }
    return requests;
  }, [identifiers, queryOptions, userId]);

  const mainQueries = useQueries({
    queries: batchedQueries,
    combine: (results) => {
      return {
        data: results
          .flatMap((result) => result.data)
          .filter((res) => res !== undefined),
        isLoading: results.some((result) => result.isLoading),
        isError: results.every((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
        isPending: results.some((result) => result.isPending),
        isRefetching: results.some((result) => result.isRefetching),
        isStale: results.every((result) => result.isStale),
        isSuccess: results.every((result) => result.isSuccess),
      };
    },
  });
  return mainQueries;
};
